import * as React from 'react'

import type { Context } from 'konva/lib/Context'
import type { Shape as KonvaShape } from 'konva/lib/Shape'
import { observer } from 'mobx-react'
import { Circle, Group, Path, Shape } from 'react-konva'

import MapViewLocationIcon from '~/client/src/shared/enums/SitemapAttributeIcon'
import { MARKER_SPEC } from '~/client/src/shared/map/markerGeometry'
import ThemeMode from '~/client/src/shared/utils/ThemeModeManager'

import {
  DEFAULT_GLYPH_STROKE_WIDTH,
  fitViewBox,
  iconNodesToPath2Ds,
  iconNodeToPathData,
  nodeFill,
  parseViewBox,
} from '../../utils/iconGeometry'
import { getMarkerGlyph } from '../../utils/markerSpec'

import Colors from '~/client/src/shared/theme.module.scss'

const SHADOW_BLUR = 2
const SHADOW_OFFSET = { x: 0, y: 0 }
const SHADOW_OPACITY = 0.3

const { getHEXColor } = ThemeMode

interface IProps {
  type: MapViewLocationIcon
  color: string
  children?: React.ReactNode
}

// Draws a location marker from the same ICON_DATA and MARKER_SPEC the georeferenced markers
// rasterize, replacing a bespoke component and a hand-scaled path constant per location type.
// fitViewBox derives the scale and offset those constants held by hand.
const KonvaLocationIcon = ({
  type,
  color,
  children,
  ...rest
}: IProps & any) => {
  const icon = getMarkerGlyph(type)

  const { outerSize, iconSize, borderWidth, borderColor, iconColor } =
    MARKER_SPEC
  const center = outerSize / 2

  // The art is the marker, filling the circle's box. konva's Path fills nonzero only, which
  // would fill evenodd holes solid, so the nodes go to the canvas directly; hit-tested as a tile.
  if (icon.isPictogram) {
    const fit = fitViewBox(icon.viewBox, outerSize, center, center)
    const [vbX, vbY, vbW, vbH] = parseViewBox(icon.viewBox)
    // Read once: a sign's art can land between this render and a draw, and the draw must pair
    // these nodes with these paths. The observer re-renders with the art when it lands.
    const nodes = icon.nodes
    const paths = iconNodesToPath2Ds(nodes)

    return (
      <Group {...rest} offsetX={center} offsetY={center}>
        {children}
        <Shape
          x={fit.x}
          y={fit.y}
          scaleX={fit.scale}
          scaleY={fit.scale}
          sceneFunc={(context: Context) =>
            nodes.forEach(([, attrs], index) => {
              context._context.fillStyle = nodeFill(attrs.fill, iconColor)
              context._context.fill(
                paths[index],
                attrs.fillRule === 'evenodd' ? 'evenodd' : 'nonzero',
              )
            })
          }
          hitFunc={(context: Context, shape: KonvaShape) => {
            context.beginPath()
            context.rect(vbX, vbY, vbW, vbH)
            context.closePath()
            context.fillStrokeShape(shape)
          }}
        />
      </Group>
    )
  }

  // Stroke straddles the path, so pull the fill in by half the border to land on outerSize —
  // the same arithmetic the canvas rasterizer uses.
  const fillRadius = center - borderWidth / 2
  const { scale, x, y } = fitViewBox(icon.viewBox, iconSize, center, center)

  return (
    <Group {...rest} offsetX={center} offsetY={center}>
      {children}
      <Circle
        radius={fillRadius}
        x={center}
        y={center}
        fill={getHEXColor(color)}
        stroke={borderColor}
        strokeWidth={borderWidth}
        shadowColor={getHEXColor(Colors.neutral0)}
        shadowBlur={SHADOW_BLUR}
        shadowOffset={SHADOW_OFFSET}
        shadowOpacity={SHADOW_OPACITY}
      />
      {icon.nodes.map(([tag, attrs], index) => {
        // Same rule the canvas rasterizer and the DOM follow: a node carrying a fill is
        // filled, everything else is stroked, so one glyph can do both. strokeWidth is in
        // grid units because the scale applies to it too.
        const isFilled = !!attrs.fill && attrs.fill !== 'none'

        return (
          <Path
            key={index}
            data={iconNodeToPathData(tag, attrs)}
            x={x}
            y={y}
            scaleX={scale}
            scaleY={scale}
            fill={isFilled ? iconColor : undefined}
            stroke={isFilled ? undefined : iconColor}
            strokeWidth={isFilled ? undefined : DEFAULT_GLYPH_STROKE_WIDTH}
            lineCap="round"
            lineJoin="round"
          />
        )
      })}
    </Group>
  )
}

export default observer(KonvaLocationIcon)
